import { useEffect, useRef, useCallback, MutableRefObject } from 'react'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import mapboxgl from 'mapbox-gl'
import { DrawingMode } from './MapDraw.types'

const calculateDistance = (
  coord1: [number, number],
  coord2: [number, number]
): number => {
  const [lon1, lat1] = coord1
  const [lon2, lat2] = coord2

  const R = 6371000
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

const formatDistance = (meters: number): string => {
  if (meters < 1000) {
    return `${meters.toFixed(1)}m`
  }
  return `${(meters / 1000).toFixed(2)}km`
}

interface UseDrawMeasurementParams {
  map: mapboxgl.Map | null
  drawRef: MutableRefObject<MapboxDraw | null>
  currentModeRef: MutableRefObject<DrawingMode>
  currentMode: DrawingMode
  enabled: boolean
  completedDrawingIds: Set<string>
}

const TOOLTIP_STYLES = [
  'position:fixed',
  'pointer-events:none',
  'z-index:10000',
  'background:rgba(50,50,50,0.92)',
  'color:#fff',
  'border-radius:8px',
  'padding:4px 8px',
  'font-size:13px',
  'font-weight:500',
  'white-space:nowrap',
  'backdrop-filter:blur(4px)',
  'transform:translate(16px,-50%)',
  'display:none',
].join(';')

export const useDrawMeasurement = ({
  map,
  drawRef,
  currentModeRef,
  currentMode,
  enabled,
  completedDrawingIds,
}: UseDrawMeasurementParams): void => {
  const completedIdsRef = useRef(completedDrawingIds)
  const tooltipRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    completedIdsRef.current = completedDrawingIds
  }, [completedDrawingIds])

  const getTooltip = useCallback(() => {
    if (!tooltipRef.current) {
      const el = document.createElement('div')
      el.setAttribute('style', TOOLTIP_STYLES)
      document.body.appendChild(el)
      tooltipRef.current = el
    }
    return tooltipRef.current
  }, [])

  const hideTooltip = useCallback(() => {
    if (tooltipRef.current) {
      tooltipRef.current.style.display = 'none'
    }
  }, [])

  const handleMouseMove = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      const draw = drawRef.current
      const mapInstance = map
      if (!draw || !mapInstance) return

      const mode = currentModeRef.current
      if (mode !== 'line' && mode !== 'polygon' && mode !== '3d') {
        hideTooltip()
        return
      }

      const rect = mapInstance.getContainer().getBoundingClientRect()
      const tooltip = getTooltip()

      const allFeatures = draw.getAll()
      const inProgress = allFeatures.features.find(
        (f) => !completedIdsRef.current.has(f.id as string)
      )

      if (!inProgress) {
        tooltip.style.display = 'none'
        return
      }

      const cursorPos: [number, number] = [e.lngLat.lng, e.lngLat.lat]
      const geom = inProgress.geometry
      let distance = ''

      if (mode === 'line' && geom.type === 'LineString') {
        const coords = geom.coordinates as [number, number][]
        // MapboxDraw includes cursor as last coord, so last placed vertex is second-to-last
        if (coords.length < 2) {
          tooltip.style.display = 'none'
          return
        }

        const lastVertex = coords[coords.length - 2] as [number, number]
        distance = formatDistance(calculateDistance(lastVertex, cursorPos))
      } else if (
        (mode === 'polygon' || mode === '3d') &&
        geom.type === 'Polygon'
      ) {
        const ring = geom.coordinates[0] as [number, number][]
        // Remove closing duplicate
        const vertices =
          ring.length > 1 &&
          ring[0][0] === ring[ring.length - 1][0] &&
          ring[0][1] === ring[ring.length - 1][1]
            ? ring.slice(0, -1)
            : ring

        // MapboxDraw includes cursor as last vertex
        if (vertices.length < 2) {
          tooltip.style.display = 'none'
          return
        }

        const lastVertex = vertices[vertices.length - 2]
        distance = formatDistance(calculateDistance(lastVertex, cursorPos))
      }

      if (distance) {
        tooltip.textContent = distance
        tooltip.style.left = `${e.point.x + rect.left}px`
        tooltip.style.top = `${e.point.y + rect.top}px`
        tooltip.style.display = 'block'
      } else {
        tooltip.style.display = 'none'
      }
    },
    [map, drawRef, currentModeRef, getTooltip, hideTooltip]
  )

  // Attach/detach listeners
  useEffect(() => {
    if (!map || !enabled) return

    const isDrawingMode =
      currentMode === 'line' ||
      currentMode === 'polygon' ||
      currentMode === '3d'
    if (!isDrawingMode) {
      hideTooltip()
      return
    }

    map.on('mousemove', handleMouseMove)
    const canvas = map.getCanvas()
    canvas.addEventListener('mouseout', hideTooltip)

    return () => {
      map.off('mousemove', handleMouseMove)
      canvas.removeEventListener('mouseout', hideTooltip)
      hideTooltip()
    }
  }, [map, enabled, currentMode, handleMouseMove, hideTooltip])

  // Cleanup DOM element on unmount
  useEffect(() => {
    return () => {
      if (tooltipRef.current) {
        tooltipRef.current.remove()
        tooltipRef.current = null
      }
    }
  }, [])
}
